import { lazy, Suspense } from "react";
import { useAuthContext } from "providers/Auth";
import { Link, useNavigate } from "react-router-dom";
import { NavLink } from "./NavLink";
import {
  boomScreenLinks,
  boomReportLinks,
  globalLinks,
  Links
} from "../constants";
import { Check, HelpCircle, LifeBuoy01, XClose } from "untitledui-js-base";
import { Menu } from "./Menu";
import { useIntercom } from "react-use-intercom";
import { useProgressContext } from "providers/ProgressContext";
import { Loader } from "shared/components/Loader";
import { CsvUploadStatus } from "providers/ProgressContext/slices/UploadCsvSlice/types";
import { useCountMetaRentReportingController } from "views/rent-reporting/controllers/general-meta-controller";
import { cx } from "shared/utils";
const Notifications = lazy(() => import("./Notifications"));
import logoSquare from "shared/branding/logo-square.svg";
import { IconButton, IconButtonSize } from "shared/components/IconButton";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { shouldShowGlobalLink } from "../utils";
import { PartnerFeatures } from "shared/types";

interface AsideProps {
  isOpenFromMobile: boolean;
  isDefaultCollapsedPage: boolean;
  setIsOpenFromMobile: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Aside = ({
  isOpenFromMobile,
  setIsOpenFromMobile,
  isDefaultCollapsedPage
}: AsideProps) => {
  const navigate = useNavigate();
  const { type, partner, permissions, partnerFeatures } = useAuthContext();
  const { subscriptionsQueue, uploadCsv } = useProgressContext();
  const { needToReportCountMeta, pendingCountMeta } =
    useCountMetaRentReportingController();
  const { show } = useIntercom();
  const hasChat = partnerFeatures.includes(PartnerFeatures.Chat);
  const analyticsAvailable = useFeatureFlagEnabled("analytics-tab");

  const { Units, Properties, Property } = useUnitsPropertiesDictionary();

  const nameOverrides: Record<string, string> = {
    Units: Units,
    Properties: Properties,
    "Property groups": `${Property} groups`
  };

  return (
    <aside
      className={cx(
        isOpenFromMobile
          ? "w-[240px] left-0"
          : "w-[80px] left-[-240px] md:left-0",
        "flex no-print transition-all top-0 absolute z-[14] h-screen flex-col border-r border-alpha-100 bg-white  md:hover:w-[240px]",
        isDefaultCollapsedPage
          ? ""
          : "lg:static lg:w-[240px] lg:min-w-[240px] lg:max-w-[240px]"
      )}
    >
      <div className="flex shrink-0">
        {isOpenFromMobile ? (
          <IconButton
            Icon={XClose}
            borderLess
            size={IconButtonSize.SM}
            className="m-4 h-5 w-5 stroke-content-200"
            onClick={() => setIsOpenFromMobile(false)}
          />
        ) : (
          <Link to="/" className="mb-8 ml-6 mt-4 block">
            <img src={logoSquare} className="h-[30px] w-[30px]" />
          </Link>
        )}
      </div>
      <div className="hide-scroll flex grow flex-col overflow-y-auto">
        <nav className="flex h-full flex-col">
          {type.isBoomScreen || type.isRentReporting ? (
            <div className="mb-6 border-b border-solid border-alpha-40 pb-6 pl-4 pr-5">
              {globalLinks
                .filter(({ name }) => shouldShowGlobalLink(name, type, partner))
                .map(({ to, name, Icon }) => {
                  return (
                    <NavLink
                      to={to}
                      Icon={Icon}
                      key={"globallinks" + to + name}
                    >
                      {nameOverrides[name] || name}
                    </NavLink>
                  );
                })}
            </div>
          ) : null}
          {type.isBoomScreen ? (
            <div className="hideScroll mb-6 border-b border-solid border-alpha-40 pb-6 pl-4 pr-5">
              <p
                className={cx(
                  "mb-2 ml-3 hidden text-xs font-medium text-content-200",
                  isDefaultCollapsedPage ? "" : " lg:block"
                )}
              >
                BoomScreen
              </p>
              {boomScreenLinks
                .filter((link) => {
                  if (!analyticsAvailable) return link.name !== "Analytics";

                  return true;
                })
                .reduce((acc, { to, name, Icon }) => {
                  if (
                    !(
                      type.isPropertyManager &&
                      name === Links.SCREENING_SETTINGS
                    )
                  )
                    acc.push(
                      <NavLink
                        to={to}
                        Icon={Icon}
                        key={"boomscreen" + to + name}
                      >
                        {name}
                      </NavLink>
                    );

                  return acc;
                }, [] as JSX.Element[])}
              {hasChat ? (
                <Suspense fallback={null}>
                  <Notifications />
                </Suspense>
              ) : null}
            </div>
          ) : null}
          {(type.isRentReporting || type.isSDK) &&
          permissions.canViewBoomReport ? (
            <div className="mb-6 border-solid border-alpha-40 pl-4 pr-5">
              <p
                className={cx(
                  "mb-2 ml-3 hidden text-xs font-medium text-content-200",
                  isDefaultCollapsedPage ? "" : " lg:block"
                )}
              >
                BoomReport
              </p>

              {boomReportLinks.map(({ to, name, Icon }) => (
                <NavLink to={to} Icon={Icon} key={"boomreport" + to + name}>
                  <div className="flex gap-2 items-center">
                    {name}{" "}
                    {name === Links.RENT_REPORTING &&
                      ((type.isSDK
                        ? pendingCountMeta.data
                        : needToReportCountMeta.data) || 0) > 0 && (
                        <div className="flex items-center justify-center rounded bg-danger-500 text-xxs text-background-1000 px-2">
                          {type.isSDK
                            ? pendingCountMeta.data
                            : needToReportCountMeta.data}
                        </div>
                      )}
                  </div>
                </NavLink>
              ))}
            </div>
          ) : null}
        </nav>
      </div>
      <div className="flex shrink-0 flex-col justify-end">
        {subscriptionsQueue.isSyncing ? (
          <div className="pl-4 pr-6">
            <div className="flex items-center gap-3 overflow-x-hidden p-3">
              <div className="shrink-0">
                <Loader size={20} />
              </div>
              <p className="text-xs text-content-500">
                Subscriptions queued: {subscriptionsQueue.amount}
              </p>
            </div>
          </div>
        ) : uploadCsv.status === CsvUploadStatus.Parsing ? (
          <Link to="/rent-reporting/upload">
            <div className="h-14 border-y border-alpha-100 bg-alpha-40 pl-4 pr-6">
              <div className="flex items-center gap-3 overflow-x-hidden p-3">
                <div className="mt-1 shrink-0 self-start">
                  <Loader size={20} />
                </div>
                <p className="text-xs text-content-500">
                  Analyzing rent reporting CSV file...
                </p>
              </div>
            </div>
          </Link>
        ) : uploadCsv.status === CsvUploadStatus.Parsed ? (
          <Link to="/rent-reporting/upload">
            <div className="h-14 border-y border-alpha-100 bg-success-50 pl-4 pr-6">
              <div className="flex items-center gap-3 overflow-x-hidden p-3">
                <div className="mt-1.5 flex h-5 w-5 shrink-0  items-center justify-center self-start rounded-full bg-success-600">
                  <Check size="14px" className="stroke-white stroke-[2px]" />
                </div>
                <p className="text-xs text-success-800">
                  Rent reporting CSV file analysis ready to review
                </p>
              </div>
            </div>
          </Link>
        ) : uploadCsv.status === CsvUploadStatus.Executing ? (
          <Link to="/rent-reporting/upload">
            <div className="h-14 border-y border-alpha-100 bg-alpha-40 pl-4 pr-6">
              <div className="flex items-center gap-3 overflow-x-hidden p-3">
                <div className="mt-1 shrink-0 self-start">
                  <Loader size={20} />
                </div>
                <p className="text-xs text-content-500">
                  Processing rent reporting CSV file...
                </p>
              </div>
            </div>
          </Link>
        ) : uploadCsv.status === CsvUploadStatus.Executed ? (
          <div
            className="h-14 border-y border-alpha-100 bg-success-50 pl-4 pr-6 cursor-pointer"
            onClick={() => {
              navigate("/rent-reporting/upload", {
                state: {
                  review: true
                }
              });
            }}
          >
            <div className="flex items-center gap-3 overflow-x-hidden p-3">
              <div className="mt-1.5 flex h-5 w-5 shrink-0  items-center justify-center self-start rounded-full bg-success-600">
                <Check size="14px" className="stroke-white stroke-[2px]" />
              </div>
              <p className="text-xs text-success-800">
                Rent reporting CSV file processed successfully
              </p>
            </div>
          </div>
        ) : null}

        <div className="mb-0.5 pl-4 pr-6">
          <NavLink
            target="_blank"
            rel="noopener"
            to="https://boomhub.notion.site/Boom-Partner-Resource-Hub-aead47527444438c888036973dc2148e"
            Icon={HelpCircle}
          >
            Resource hub
          </NavLink>
        </div>
        <div className="w-full pl-4 pr-6">
          <NavLink Icon={LifeBuoy01} onClick={show}>
            Support
          </NavLink>
        </div>
        <Menu />
      </div>
    </aside>
  );
};
